import { Coupon, CouponType } from "@greenflagdate/shared";
import {
  Box,
  Button,
  IconButton,
  Input,
  PopoverBody,
  PopoverContent,
  PopoverPositioner,
  PopoverRoot,
  PopoverTrigger,
} from "@chakra-ui/react";
import { Alert } from "../Alert/Alert";
import { useCallback, useState } from "react";
import { apiReq } from "../../lib/apiReq";
import { useReq } from "@larner.dev/use-req";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

interface SparkCouponButtonProps {
  sparkId: number;
  onCouponApplied: (coupon: Coupon) => void;
}

export const SparkCouponButton = ({
  sparkId,
  onCouponApplied,
}: SparkCouponButtonProps) => {
  const [validateCouponReq, validateCouponReqState] = useReq<Coupon>(apiReq);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState("");
  const applyCoupon = useCallback<React.FormEventHandler>(
    async (e) => {
      e.preventDefault();
      setCouponError("");
      if (!couponCode) {
        return setPopoverOpen(false);
      }
      const couponResult = await validateCouponReq.post("/coupon/check", {
        type: CouponType.Spark,
        code: couponCode,
        referenceId: sparkId,
      });
      if (!couponResult.success) {
        if (couponResult.error.code === "EXPIRED") {
          setCouponError("This coupon has expired");
        } else if (couponResult.error.code === "NOT_FIRST_SPARK") {
          setCouponError(
            "This coupon is only valid if you haven't been to an event before"
          );
        } else {
          setCouponError("The coupon wasn't found");
        }
      } else {
        onCouponApplied(couponResult.result);
        return setPopoverOpen(false);
      }
    },
    [couponCode, onCouponApplied, sparkId, validateCouponReq]
  );
  return (
    <PopoverRoot
      open={popoverOpen}
      onOpenChange={({ open }) => {
        setPopoverOpen(open);
        if (!open) {
          setCouponCode("");
          setCouponError("");
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="plain"
          textDecoration="underline"
          textTransform="none"
          display="inline-block"
          fontWeight="normal"
          fontSize="0.75rem"
          border="0"
          padding="0"
          background="transparent"
          lineHeight="1"
          height="1rem"
          color="brand.blue.primary"
          _hover={{ color: "brand.blue.light" }}
        >
          Apply Coupon Code
        </Button>
      </PopoverTrigger>
      <PopoverPositioner>
        <PopoverContent padding="0.5rem">
          <PopoverBody padding="0">
            {couponError && (
              <Alert
                intent="danger"
                title={couponError}
                style={{ marginBottom: "0.5rem" }}
              />
            )}
            <Box as="form" display="flex" gap="0.5rem" onSubmit={applyCoupon}>
              <Input
                placeholder="Coupon Code"
                autoFocus
                size="sm"
                value={couponCode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCouponCode(e.target.value)
                }
              />
              <IconButton
                aria-label="Apply coupon"
                type="submit"
                size="sm"
                variant="outline"
                color="brand.green.light"
                loading={validateCouponReqState.loading}
              >
                <FontAwesomeIcon icon={faCheck} />
              </IconButton>
            </Box>
          </PopoverBody>
        </PopoverContent>
      </PopoverPositioner>
    </PopoverRoot>
  );
};
